import React, { useCallback, useEffect, useState } from 'react';
import { AutocompletePatientResponse, Patient, PatientService } from 'src/services/patient';
import { Box } from './UI/Box';
import { Search } from './Search/Search';
import { PatientList } from './PatientList';
import { usePrescriberContext, USER_GROUPS } from 'src/providers/PrescriberProvider';
import { ORDER_BY, PrescriberService, SearchParams } from 'src/services/prescriber';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';
import { ORDER_TYPES } from 'src/services/prescriptionHistory';
import { Form, Formik } from 'formik';
import { Yup } from 'src/helpers/Yup';
import { testIfStringOnlyHasNumber } from 'src/helpers/number';
import useLoading from 'src/helpers/useLoading';
import useAsyncSetFieldValue from 'src/helpers/useAsyncSetFieldValue';
import { EmptyPatientView } from './EmptyPatientView';
import { onlyDigits } from 'src/helpers/string';
import { validateCPF } from 'src/helpers/cpf';
import { useNavigate } from 'react-router-dom';
import Typography from './Typography';
import { Flex } from './UI/Flex';
import { Button } from './UI/Button';
import AlertIcon from './Icons/AlertIcon';
import { useMediaQuery } from '@chakra-ui/react';
import { trackPatientNotFoundRegisterClick } from 'src/services/tracking/events';

interface PatientListProps {
  onEnterCallback?(): void;
  onCreatePatient(): void;
  onImportPatient?(): void;
  onFocus(): void;
  onBlur(): void;
  onClickCallback(patient: Patient): Promise<void>;
  showActionButtons: boolean;

  onEdit?(patientId: string): void;
  onInitPrescription?(patientId: string): Promise<void>;
  onDelete?(patientId: string): Promise<void>;
  userGroup: USER_GROUPS;
}

const SearchSchema = Yup.object().shape({
  searchString: Yup.string(),
  shouldCallAction: Yup.bool().notRequired(),
});

const DEFAULT_SEARCH_PAYLOAD = {
  orderBy: ORDER_BY.CREATE_AT,
  orderType: ORDER_TYPES.DESC,
  page: 1,
  limit: 20,
};

export function PatientSearchList(props: PatientListProps) {
  const [lessThanMediumScreen] = useMediaQuery('(max-width: 1200px)');
  const { prescriberId, patients, setPatients, setUserDoesNotHaveAnyPatients, userDoesNotHaveAnyPatients } =
    usePrescriberContext();
  const { openToast } = useFeedbackContext();
  const [loading, runWithLoading] = useLoading();
  const navigate = useNavigate();

  const [asyncSetShouldPaginate] = useAsyncSetFieldValue('shouldPaginate');
  const [asyncSetPage] = useAsyncSetFieldValue('page');

  const [hasNextPage, setHasNextPage] = useState(false);
  const [autocompleteData, setAutocompleteData] = useState<AutocompletePatientResponse | null>(null);
  const [searchedCPF, setSearchedCPF] = useState<string>('');
  const [invalidCPFError, setInvalidCPFError] = useState(false);

  const searchPatients = useCallback(
    async (shouldPaginate: boolean, searchParams?: SearchParams) => {
      if (!prescriberId) return null;

      // Limpa os dados de autocomplete ao fazer nova busca
      setAutocompleteData(null);
      setSearchedCPF('');
      setInvalidCPFError(false);

      runWithLoading(async () => {
        try {
          const payload = searchParams || DEFAULT_SEARCH_PAYLOAD;

          const data = await PrescriberService.searchPatients(prescriberId, payload);

          setHasNextPage(data.hasNextPage);

          if (shouldPaginate) {
            const oldPatients = patients || [];
            setPatients([...oldPatients, ...(data.data.patients || [])]);
          } else {
            setPatients(data.data.patients);
          }

          if ((!data.data.patients || data.data.patients.length === 0) && searchParams?.document && !shouldPaginate) {
            const cpfDigits = onlyDigits(searchParams.document);

            if (validateCPF(cpfDigits)) {
              try {
                const autocompleteResult = await PatientService.autocompletePatientByCPF(prescriberId, cpfDigits);
                setAutocompleteData(autocompleteResult);
                setSearchedCPF(searchParams.document);
                setInvalidCPFError(false);
              } catch (error: any) {
                const status = error?.response?.status;
                const message = error?.response?.data?.message || '';

                if (status === 400 && message.toLowerCase().includes('cpf inválido')) {
                  setInvalidCPFError(true);
                  setAutocompleteData(null);
                  setSearchedCPF('');
                } else {
                  setAutocompleteData(null);
                  setSearchedCPF('');
                  setInvalidCPFError(false);
                }
              }
            }
          }

          return;
        } catch (error) {
          openToast('Erro ao buscar pacientes. Tente novamente em instantes.', { type: 'error', duration: 2000 });
          return null;
        }
      });
    },
    [prescriberId, runWithLoading, patients, setPatients, openToast]
  );

  useEffect(() => {
    if (!prescriberId) return;

    const run = async () => {
      runWithLoading(async () => {
        try {
          const data = await PrescriberService.searchPatients(prescriberId, DEFAULT_SEARCH_PAYLOAD);

          setPatients(data.data.patients);
          setHasNextPage(data.hasNextPage);

          if (!data.data.patients || data.data.patients.length === 0) {
            setUserDoesNotHaveAnyPatients(true);
          }
        } catch (error) {}
      });
    };

    run();
  }, [prescriberId, runWithLoading, setPatients, setUserDoesNotHaveAnyPatients]);

  if (userDoesNotHaveAnyPatients) {
    return <EmptyPatientView onClick={props.onCreatePatient} />;
  }

  return (
    <Box mt={'10px'} maxW={'1200px'} w={'100%'} px={lessThanMediumScreen ? '0px' : '150px'}>
      <Formik
        initialValues={{
          searchString: '',
          shouldCallAction: false,
          orderType: ORDER_TYPES.DESC,
          orderBy: ORDER_BY.CREATE_AT,
          selectedFilter: {
            label: 'Mais recentes primeiro',
            arrowDirection: 'down',
          },
          page: 1,
          limit: 20,
          shouldPaginate: false,
        }}
        validateOnChange={false}
        validationSchema={SearchSchema}
        onSubmit={async (values, { setFieldValue }) => {
          if (values.searchString.length === 0) {
            await searchPatients(values.page > 1, {
              ...DEFAULT_SEARCH_PAYLOAD,
              orderBy: values.orderBy,
              orderType: values.orderType,
              page: values.page,
            });

            return;
          }

          const minimalValueToSearch = 2;
          if (values.searchString.length < minimalValueToSearch && !values.shouldPaginate) return;

          let searchParams: Omit<SearchParams, 'selectedFilter'> = {
            orderBy: values.orderBy,
            orderType: values.orderType,
            page: values.page,
            limit: values.limit,
          };

          if (values.searchString) {
            if (testIfStringOnlyHasNumber(values.searchString)) {
              searchParams = {
                ...searchParams,
                document: values.searchString,
                fullName: '',
              };
            } else {
              searchParams = {
                ...searchParams,
                document: '',
                fullName: values.searchString,
              };
            }
          }

          await searchPatients(values.shouldPaginate, searchParams);

          if (values.shouldPaginate) {
            setFieldValue('shouldPaginate', false);
          }

          const existOnlyOnePatient = (patients || []).length === 1;
          if (props.onEnterCallback && existOnlyOnePatient && values.shouldCallAction) {
            props.onEnterCallback();
            setFieldValue('shouldCallAction', false);

            return;
          }
        }}
      >
        {(formikProps) => {
          return (
            <Form>
              <Box position={'sticky'} top={'0px'} zIndex={10}>
                <Search
                  onEnterCallback={() => {
                    formikProps.setFieldValue('shouldCallAction', true);
                  }}
                  onCreatePatient={props.onCreatePatient}
                  onImportPatient={props.onImportPatient}
                  onFocus={props.onFocus}
                  onBlur={props.onBlur}
                />
              </Box>

              {invalidCPFError && (
                <Flex mt={'20px'} flexDirection={'column'} alignItems={'center'}>
                  <Flex
                    borderRadius={'15px'}
                    padding={'20px'}
                    flexDirection={'column'}
                    w={{ base: '100%', sm: 'auto' }}
                    maxW={{ base: '100%', sm: '400px' }}
                  >
                    <Flex alignItems={'center'} mb={'8px'}>
                      <Box mr={'8px'}>
                        <AlertIcon variantColor="notification.alert" width={20} />
                      </Box>
                      <Typography model="body" size="normal" weight="bold">
                        CPF inválido
                      </Typography>
                    </Flex>
                    <Typography model="body" size="normal" weight="regular">
                      Não foi possível validar este CPF. Confira o número e tente novamente.
                    </Typography>
                  </Flex>
                </Flex>
              )}

              {/* Mensagem quando CPF encontrado mas não está na lista */}
              {autocompleteData && (
                <Flex mt={'20px'} flexDirection={'column'} alignItems={'center'}>
                  <Flex
                    bgColor={'#F9DCEE'}
                    borderRadius={'15px'}
                    padding={'20px'}
                    flexDirection={'column'}
                    mb={'16px'}
                    w={{ base: '100%', sm: 'auto' }}
                    maxW={{ base: '100%', sm: '400px' }}
                  >
                    <Typography model="body" size="normal" weight="regular" color={'#3D1454'}>
                      Encontramos o CPF, mas o paciente ainda não está na sua lista.
                    </Typography>
                    <Typography model="body" size="normal" weight="bold" color={'#3D1454'}>
                      Para adicioná-lo, clique no botão abaixo e complete as informações.
                    </Typography>
                  </Flex>

                  <Button
                    variant="solid"
                    w={{ base: '100%', sm: 'auto' }}
                    maxW={{ base: '100%', sm: '400px' }}
                    h="56px"
                    onClick={() => {
                      trackPatientNotFoundRegisterClick(prescriberId || undefined);
                      navigate('/inicio/cadastro-completo-paciente', {
                        state: {
                          cpf: searchedCPF,
                          autocompleteData: autocompleteData,
                        },
                      });
                    }}
                  >
                    Adicionar paciente
                  </Button>
                </Flex>
              )}

              <PatientList
                patientList={patients}
                isLoading={loading}
                onClickCallback={props.onClickCallback}
                showActionButtons={props.showActionButtons}
                onDelete={props.onDelete}
                onEdit={props.onEdit}
                onInitPrescription={props.onInitPrescription}
                onLoadMore={async () => {
                  await asyncSetShouldPaginate(true, formikProps.setFieldValue);
                  await asyncSetPage(formikProps.values.page + 1, formikProps.setFieldValue);
                  formikProps.handleSubmit();
                }}
                hasNextPage={hasNextPage}
                userGroup={props.userGroup}
              />
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
}
